/**
 * Man-overboard casualty drift. The moment someone goes in, the boat sails on,
 * but the person doesn't stay put either: they drift with the tidal stream (and
 * a little with the surface wind). The marker we dropped is where they WERE; this
 * projects where they likely ARE now, so the return leg aims at the drifting
 * datum, not the cold mark. A person in the water makes very little leeway of
 * their own, so the tide does almost all of it.
 */
import { rhumbDestination, rhumbDistanceNm, rhumbBearingDeg } from '@/nav';
import { leewayRateNmPerHr, type CasualtyType } from './mobLeeway';

export interface DriftComponent {
  /** Direction it sets TOWARDS, degrees true. */
  setDeg: number;
  /** Rate, knots. */
  rateKn: number;
}

export interface MobDriftInput {
  /** Where the casualty went in, [lon, lat]. */
  mobPosition: [number, number];
  /** Time since they went in, minutes. */
  elapsedMin: number;
  /** Average tidal stream over the interval. */
  tide: DriftComponent;
  /** Optional surface-wind drift of a person in the water (small). */
  windDrift?: DriftComponent;
}

export interface MobDrift {
  /** Estimated current position of the casualty, [lon, lat]. */
  datum: [number, number];
  /** How far they've drifted from the drop mark, nm. */
  driftNm: number;
  /** Direction of the net drift, degrees true. */
  driftSetDeg: number;
}

export function mobDrift(input: MobDriftInput): MobDrift {
  const hours = Math.max(0, input.elapsedMin) / 60;
  let datum = rhumbDestination(input.mobPosition, input.tide.setDeg, input.tide.rateKn * hours);
  if (input.windDrift) {
    datum = rhumbDestination(datum, input.windDrift.setDeg, input.windDrift.rateKn * hours);
  }
  const driftNm = rhumbDistanceNm(input.mobPosition, datum);
  return {
    datum,
    driftNm,
    driftSetDeg: driftNm < 1e-9 ? input.tide.setDeg : rhumbBearingDeg(input.mobPosition, datum),
  };
}

/** Range and bearing from the boat to the drifting casualty datum. */
export function mobRangeBearing(
  boat: [number, number],
  datum: [number, number],
): { rangeNm: number; bearingDeg: number } {
  return { rangeNm: rhumbDistanceNm(boat, datum), bearingDeg: rhumbBearingDeg(boat, datum) };
}

// -----------------------------------------------------------------------------
// RNLI Card 10 casualty datum: tidal set + leeway (downwind), interpolated
// -----------------------------------------------------------------------------

/**
 * How long the RNLI Card 10 datum method stays trustworthy, minutes. The card is
 * built for the first hour of drift: after that the tide has usually turned and
 * the wind shifted, and a single averaged vector no longer describes where the
 * casualty went. Past this we still show a datum, but flag it as unreliable.
 */
export const DRIFT_RELIABLE_MIN = 60;

export interface CasualtyDriftInput {
  /** Drift start position (the MOB last-known position), [lon, lat]. */
  dsp: [number, number];
  /** Time since the casualty went in, minutes (elapsed from the drift start). */
  elapsedMin: number;
  /**
   * Average tidal stream over the elapsed interval, set (towards, °T) and rate
   * (kn). Averaged from the app's tidal-stream data by the caller.
   */
  tide: DriftComponent;
  /** The casualty type from the RNLI leeway table. */
  casualty: CasualtyType;
  /** Wind direction it blows FROM, degrees true. */
  windFromDeg: number;
  /** Wind speed, knots. */
  windKn: number;
}

export interface CasualtyDriftResult extends MobDrift {
  /** The leeway component we applied: downwind set and the card's rate (kn). */
  leeway: DriftComponent;
  /** Distance the leeway alone accounts for, nm. */
  leewayNm: number;
  /** Distance the tide alone accounts for, nm. */
  tideNm: number;
  /** True once past DRIFT_RELIABLE_MIN, so the UI can show the RNLI caveat. */
  beyondReliable: boolean;
}

/**
 * Estimate the casualty's drifted datum by the RNLI Card 10 method: lay the
 * averaged tidal set from the drop position, then add the leeway downwind. The
 * leeway rate comes from the card for the casualty type at this wind speed
 * (interpolated between the 5-knot columns); leeway acts WITH the wind, so its
 * set is the wind's from-direction plus 180°.
 *
 * Honest about its limits: this is the card's first-hour estimate on an averaged
 * tide and a steady wind. It's a datum to start a search from, not a fix. See
 * DRIFT_RELIABLE_MIN and `beyondReliable`.
 */
export function casualtyDriftEstimate(input: CasualtyDriftInput): CasualtyDriftResult {
  const hours = Math.max(0, input.elapsedMin) / 60;
  const leewayRate = leewayRateNmPerHr(input.casualty, input.windKn);
  const leewaySet = ((input.windFromDeg % 360) + 360 + 180) % 360; // downwind
  const leeway: DriftComponent = { setDeg: leewaySet, rateKn: leewayRate };
  const base = mobDrift({
    mobPosition: input.dsp,
    elapsedMin: input.elapsedMin,
    tide: input.tide,
    windDrift: leeway,
  });
  return {
    ...base,
    leeway,
    leewayNm: leewayRate * hours,
    tideNm: input.tide.rateKn * hours,
    beyondReliable: input.elapsedMin > DRIFT_RELIABLE_MIN,
  };
}

/**
 * Vector-average a set of tidal-stream samples into one set/rate component. Each
 * sample is turned into an east/north velocity, averaged, and turned back, so a
 * tide that eases and turns over the interval reduces to the mean push it
 * actually gave, not a naive average of two opposing directions. Returns a zero
 * rate for an empty set.
 */
export function averageComponents(samples: DriftComponent[]): DriftComponent {
  if (!samples.length) return { setDeg: 0, rateKn: 0 };
  let e = 0;
  let n = 0;
  for (const s of samples) {
    const r = (s.setDeg * Math.PI) / 180;
    e += s.rateKn * Math.sin(r);
    n += s.rateKn * Math.cos(r);
  }
  e /= samples.length;
  n /= samples.length;
  const rateKn = Math.hypot(e, n);
  const setDeg = rateKn < 1e-9 ? 0 : (((Math.atan2(e, n) * 180) / Math.PI) + 360) % 360;
  return { setDeg, rateKn };
}
